import React, { useState } from 'react';
import { Customer } from '../types/types';
import { apiService } from '../services/api';
import '../styles/common.css';

interface AddCustomerPopupProps {
  onClose: () => void;
  onAdd: (customer: Customer) => void;
  existingApiKeys: string[];
}

interface FormData {
  name: string;
  apikey: string;
  readBatchSize: string;
  readDelayMillis: string;
  postURL: string;
}

interface FormErrors {
  name?: string;
  apikey?: string;
  readBatchSize?: string;
  readDelayMillis?: string;
  postURL?: string;
  submit?: string;
}

export const AddCustomerPopup: React.FC<AddCustomerPopupProps> = ({ onClose, onAdd, existingApiKeys }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    apikey: '',
    readBatchSize: '',
    readDelayMillis: '',
    postURL: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // API Key validation
    if (!formData.apikey.trim()) {
      newErrors.apikey = 'API Key is required';
    } else if (existingApiKeys.includes(formData.apikey)) {
      newErrors.apikey = 'API Key already exists';
    }

    // Optional fields validation - only if they have values
    if (formData.readBatchSize && (Number(formData.readBatchSize) <= 1)) {
      newErrors.readBatchSize = 'Read batch size must be greater than 1';
    }

    if (formData.readDelayMillis && (Number(formData.readDelayMillis) <= 10)) {
      newErrors.readDelayMillis = 'Read delay must be greater than 10 milliseconds';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Create the customer object with only the fields that have values
      const customerConfig: any = {
        name: formData.name,
        apikey: formData.apikey
      };

      // Only add optional fields if they have values
      if (formData.readBatchSize.trim()) {
        customerConfig.readBatchSize = Number(formData.readBatchSize);
      }
      if (formData.readDelayMillis.trim()) {
        customerConfig.readDelayMillis = Number(formData.readDelayMillis);
      }
      if (formData.postURL.trim()) {
        customerConfig.postURL = formData.postURL;
      }

      const newCustomer: Customer = {
        ...customerConfig,
        writeCounter: 0,
        readCounter: 0,
        pendingMessages: 0
      };

      await onAdd(newCustomer);
      onClose();
    } catch (error) {
      console.error('Failed to add customer:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to add customer. Please try again.'
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h2>Add New Customer</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={handleChange('name')}
              className={errors.name ? 'error' : ''}
              placeholder="Enter customer name"
            />
            {errors.name && <div className="error-text">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="apikey">API Key *</label>
            <input
              id="apikey"
              type="text"
              value={formData.apikey}
              onChange={handleChange('apikey')}
              className={errors.apikey ? 'error' : ''}
              placeholder="Enter API key"
            />
            {errors.apikey && <div className="error-text">{errors.apikey}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="readBatchSize">Read Batch Size</label>
            <input
              id="readBatchSize"
              type="number"
              value={formData.readBatchSize}
              onChange={handleChange('readBatchSize')}
              className={errors.readBatchSize ? 'error' : ''}
              min="2"
              placeholder="Leave empty for global setting"
            />
            <small>If specified, must be greater than 1</small>
            {errors.readBatchSize && <div className="error-text">{errors.readBatchSize}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="readDelayMillis">Read Delay (ms)</label>
            <input
              id="readDelayMillis"
              type="number"
              value={formData.readDelayMillis}
              onChange={handleChange('readDelayMillis')}
              className={errors.readDelayMillis ? 'error' : ''}
              min="11"
              placeholder="Leave empty for global setting"
            />
            <small>If specified, must be greater than 10 milliseconds</small>
            {errors.readDelayMillis && <div className="error-text">{errors.readDelayMillis}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="postURL">Post URL</label>
            <input
              id="postURL"
              type="text"
              value={formData.postURL}
              onChange={handleChange('postURL')}
              className={errors.postURL ? 'error' : ''}
              placeholder="Leave empty for global setting"
            />
            {errors.postURL && <div className="error-text">{errors.postURL}</div>}
          </div>

          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="popup-footer">
            <button
              type="button"
              className="button secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={isSaving}
            >
              {isSaving ? 'Adding...' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 